import { useEffect, useRef } from 'react';
import type { LiveEvent } from '@shared/events';
import { queryClient } from './queryClient';
import { toast } from './toast';
import type { IconName } from '@/ui/icons';
import { ICON_NAMES } from '@/ui/icons';

// Live updates from the server over one event stream per tab. Changes made by teammates refresh the matching
// data straight away; new notifications and server messages appear as they happen.

type Listener = (event: LiveEvent) => void;
const listeners = new Set<Listener>();

export function useLiveEvents(handler: Listener): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const fn: Listener = (e) => ref.current(e);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
}

function handle(event: LiveEvent): void {
  if (event.type === 'invalidate') {
    for (const key of event.keys) void queryClient.invalidateQueries({ queryKey: key });
  } else if (event.type === 'notification') {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  } else if (event.type === 'toast') {
    const icon = (ICON_NAMES as readonly string[]).includes(event.icon ?? '') ? (event.icon as IconName) : undefined;
    toast(event.message, { icon });
  }
  for (const fn of listeners) fn(event);
}

/** Opens the event stream and keeps it open. Returns a function that closes it. */
export function startLive(): () => void {
  let source: EventSource | null = null;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  let hadError = false;

  const connect = () => {
    if (stopped) return;
    source = new EventSource('/api/events', { withCredentials: true });
    for (const type of ['invalidate', 'notification', 'toast'] as const) {
      source.addEventListener(type, (ev) => {
        try {
          handle(JSON.parse((ev as MessageEvent<string>).data) as LiveEvent);
        } catch (err) {
          console.error('[live] bad event', err);
        }
      });
    }
    source.addEventListener('open', () => {
      // Anything that changed while the stream was down is refetched once it is back.
      if (hadError) void queryClient.invalidateQueries();
      hadError = false;
    });
    source.addEventListener('error', () => {
      hadError = true;
      if (source?.readyState === EventSource.CLOSED) {
        // The browser gave up (for example after the session ended); check the session and try again shortly.
        void queryClient.invalidateQueries({ queryKey: ['me'] });
        clearTimeout(retry);
        retry = setTimeout(connect, 5000);
      }
    });
  };

  connect();
  return () => {
    stopped = true;
    clearTimeout(retry);
    source?.close();
  };
}
